import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

// Tipi per l'autenticazione
interface User {
  id: string;
  username: string;
  email: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

// Creazione del context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook per utilizzare il context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Provider del context
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Verifica se l'utente è autenticato
  const isAuthenticated = !!user;

  // Funzione di login
  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      
      const response = await axios.post('/api/auth/login', {
        username,
        password,
      });

      // Il backend restituisce { status: "success", token: "...", data: { user: {...} } }
      if (response.data.status === 'success') {
        const userData = response.data.data.user;
        setUser(userData);
        
        // Salva il token nel localStorage
        if (response.data.token) {
          localStorage.setItem('authToken', response.data.token);
          // Configura axios per includere il token nelle richieste future
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        }
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Funzione di logout
  const logout = () => {
    setUser(null);
    localStorage.removeItem('authToken');
    delete axios.defaults.headers.common['Authorization'];
  };

  // Verifica l'autenticazione al caricamento
  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setLoading(false);
        return;
      }

      // Configura axios con il token
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Verifica la validità del token
      const response = await axios.get('/api/auth/me');
      
      if (response.data.success) {
        setUser(response.data.user);
      } else {
        // Token non valido, rimuovilo
        logout();
      }
    } catch (error) {
      console.error('Auth check error:', error);
      // Token non valido o errore di rete, rimuovi il token
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Effetto per verificare l'autenticazione al mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Valore del context
  const value: AuthContextType = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;